import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productService, categoryService, cartService } from '../../services/api';
import { ShoppingCart, Heart, Search, Filter, ArrowRight, Star } from 'lucide-react';
import AdSlot from '../AdSlot';
import { updateMetaTags } from '../../utils/seo';

const Shop = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadInitialData();
    updateMetaTags({
      title: 'Shop Premium FMCG',
      description: 'Explore our range of meticulously crafted food and beverage products, delivered straight to your door.'
    });
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        productService.index({ active_only: 1 }),
        categoryService.index()
      ]);
      setProducts(Array.isArray(productsData) ? productsData : productsData.data || []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : categoriesData.data || []);
    } catch (error) {
      console.error('Error loading shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    if (product.variants && product.variants.length > 0) {
      navigate(`/shop/${product.slug}`);
      return;
    }
    try {
      await cartService.addItem(product.id, 1);
      setMessage({ type: 'success', text: `${product.name} added to cart!` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add to cart.' });
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = !activeCategory || p.category_id === activeCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (p.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'popular') return b.sales_count - a.sales_count;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-red-400 font-bold uppercase tracking-widest text-sm mb-4 block">Premium FMCG Portfolio</span>
          <h1 className="text-4xl md:text-6xl font-black mb-6">Quality You Can Taste</h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Discover our range of meticulously crafted food and beverage products, delivered straight to your door.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {message && (
          <div className={`fixed bottom-8 right-8 z-50 p-4 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300 ${
            message.type === 'success' ? 'bg-red-600 text-white' : 'bg-rose-600 text-white'
          }`}>
            {message.text}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row gap-6 mb-12">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search by product name or ingredients..."
              className="w-full bg-white border-2 border-slate-100 rounded-2xl py-4 pl-12 pr-4 focus:border-red-500 outline-none transition-all shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-4">
            <select 
              className="bg-white border-2 border-slate-100 rounded-2xl px-6 py-4 focus:border-red-500 outline-none shadow-sm font-medium"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest Arrivals</option>
              <option value="popular">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <aside className="lg:w-64 space-y-8">
            <div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Filter size={16} /> Categories
              </h3>
              <div className="space-y-2">
                <button 
                  onClick={() => setActiveCategory(null)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all font-medium ${
                    !activeCategory ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  All Products
                </button>
                {categories.map(cat => (
                  <button 
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all font-medium ${
                      activeCategory === cat.id ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'text-slate-600 hover:bg-white'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Newsletter Mini */}
            <div className="bg-red-600 rounded-2xl p-6 text-white shadow-xl">
              <h4 className="font-bold text-lg mb-2">Get Updates</h4>
              <p className="text-red-100 text-sm mb-4">Subscribe for exclusive offers and new launches.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-white font-bold text-sm bg-red-700 px-4 py-2 rounded-lg hover:bg-red-800 transition-colors">
                Join Now <ArrowRight size={16} />
              </Link>
            </div>

            {/* Shop Sidebar Ad Slot */}
            <AdSlot position="shop_sidebar" className="min-h-[350px]" />
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <div key={product.id} className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                  <Link to={`/shop/${product.slug}`} className="block relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img 
                      src={product.featured_image || '/placeholder-product.png'} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {product.discount_price && (
                      <div className="absolute top-4 left-4 bg-rose-500 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">
                        Sale
                      </div>
                    )}
                    {product.variants && product.variants.length > 0 && (
                      <div className="absolute top-4 right-4 bg-purple-500 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">
                        {product.variants.length} Options
                      </div>
                    )}
                  </Link>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">{product.category?.name}</span>
                      <div className="flex items-center gap-1 text-amber-400">
                        <Star size={12} fill="currentColor" />
                        <span className="text-slate-400 text-[10px] font-bold">{product.rating || '5.0'}</span>
                      </div>
                    </div>
                    <Link to={`/shop/${product.slug}`} className="block">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">{product.name}</h3>
                    </Link>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-6">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5">Selling Price</div>
                        <span className="text-2xl font-black text-slate-900">₹{product.discount_price || product.price}</span>
                        {product.discount_price && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400 line-through">MRP: ₹{product.price}</span>
                            <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                              {Math.round(((product.price - product.discount_price) / product.price) * 100)}% OFF
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleAddToCart(product)}
                          className="w-12 h-12 bg-slate-900 text-white rounded-xl flex items-center justify-center hover:bg-red-600 transition-all shadow-lg hover:shadow-red-200 group/btn"
                        >
                          <ShoppingCart size={20} className="group-hover/btn:scale-110 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {filteredProducts.length === 0 && (
              <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                <Search className="mx-auto text-slate-200 mb-4" size={48} />
                <h3 className="text-xl font-bold text-slate-900">No products found</h3>
                <p className="text-slate-500">Try adjusting your filters or search query.</p>
                <button 
                  onClick={() => { setActiveCategory(null); setSearchQuery(''); }}
                  className="mt-6 text-red-600 font-bold hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
